// PRPathways.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, MapPin, Users, ChevronDown, ChevronUp } from 'lucide-react';

const PRPathways = () => {
  const [expandedPathway, setExpandedPathway] = useState(null);

  const pathways = [
    {
      icon: Zap,
      title: 'Express Entry',
      subtitle: 'Federal Skilled Trades, Federal Skilled Worker, Canadian Experience Class',
      description: 'The busiest route into Canadian PR, and the quickest: applications can be decided in as little as 6 months.',
      details: {
        overview: 'Three federal economic immigration programmes are run through a single points-ranked pool known as Express Entry.',
        programs: [
          {
            name: 'Federal Skilled Worker Program (FSWP)',
            requirements: 'Aimed at skilled workers whose experience was gained outside Canada. You need at least 1 year of continuous full-time work in a skilled occupation, meaning NOC TEER 0, 1, 2 or 3.'
          },
          {
            name: 'Canadian Experience Class (CEC)',
            requirements: 'Built for people who have already worked in Canada. The bar is 1 year of skilled Canadian work experience gained within the last 3 years.'
          },
          {
            name: 'Federal Skilled Trades Program (FSTP)',
            requirements: 'A route for qualified trades. You must show 2 years of full-time work in a skilled trade over the last 5 years, together with either a valid job offer or a certificate of qualification.'
          }
        ],
        keyPoints: [
          'Processing time: 6 months or less',
          'Most applicants do not need a job offer',
          'Draws are held roughly every 2 weeks',
          'Your Comprehensive Ranking System (CRS) score decides whether you are invited',
          'Cut-off scores move; recent draws have sat around 470-500'
        ]
      }
    },
    {
      icon: MapPin,
      title: 'Provincial Nominee Programs (PNPs)',
      subtitle: 'Alberta, British Columbia, Manitoba, Ontario, Saskatchewan and others',
      description: 'Routes run by individual provinces, which nominate applicants intending to settle in that part of the country.',
      details: {
        overview: 'Every province apart from Quebec operates its own nominee programme, with streams shaped around the occupations and skills that province is short of.',
        programs: [
          {
            name: 'Ontario Immigrant Nominee Program (OINP)',
            requirements: 'Streams here include Employer Job Offer, French-Speaking Skilled Worker and Human Capital Priorities. Ontario runs targeted draws, keyed to particular NOC codes and CRS ranges.'
          },
          {
            name: 'British Columbia PNP (BC PNP)',
            requirements: 'Two entry points, Skills Immigration and Express Entry BC. Most of them expect a job offer from an employer in the province, and the tech sector has draws of its own.'
          },
          {
            name: 'Alberta Immigrant Nominee Program (AINP)',
            requirements: 'Runs an Alberta Opportunity Stream alongside an Express Entry Stream. Both lean towards candidates holding an Alberta job offer or work history in the province.'
          },
          {
            name: 'Saskatchewan Immigrant Nominee Program (SINP)',
            requirements: 'The International Skilled Worker category splits into Occupation In-Demand and Express Entry sub-categories. Not every stream asks for a job offer.'
          },
          {
            name: 'Manitoba Provincial Nominee Program (MPNP)',
            requirements: 'Applications go through either Skilled Workers in Manitoba or Skilled Workers Overseas. In most cases you will need a job offer or an established connection to Manitoba.'
          }
        ],
        keyPoints: [
          'A provincial nomination is worth 600 CRS points',
          'Eligibility criteria differ from province to province',
          'A job offer is essential in some streams and optional in others',
          'Processing time: 15-19 months in total',
          'Often the strongest route when your CRS score is modest'
        ]
      }
    },
    {
      icon: Users,
      title: 'Family Sponsorship',
      subtitle: 'Partners and spouses, dependent children, parents, grandparents',
      description: 'Hold Canadian citizenship or PR status and you may sponsor qualifying relatives for permanent residency.',
      details: {
        overview: 'This route exists so that the close relatives of citizens and permanent residents can settle in Canada alongside them.',
        programs: [
          {
            name: 'Spousal and Common-Law Partner Sponsorship',
            requirements: 'The sponsor has to be 18 or over and hold citizenship or PR status. Applications can be filed from inside or outside Canada. Evidence that the relationship is genuine sits at the centre of the case, and minimum income rules apply, though not to spouse or partner sponsorship itself.'
          },
          {
            name: 'Dependent Children Sponsorship',
            requirements: 'Covers children under 22 who are neither married nor in a common-law relationship. From 22 upwards, a child qualifies only where a physical or mental condition leaves them dependent.'
          },
          {
            name: 'Parents and Grandparents Program (PGP)',
            requirements: 'Sponsors have to show the minimum necessary income across 3 consecutive years. Intake is capped and places are allocated by lottery. Where that is not realistic, the Super Visa offers an alternative for longer visits.'
          }
        ],
        keyPoints: [
          'Spousal sponsorship is processed in around 12 months',
          'Parent and grandparent cases run 20-24 months',
          'Approval grants the relative full permanent resident status',
          'The sponsor signs an undertaking to support the person sponsored',
          'Nothing is scored on points here; proving the relationship is what counts'
        ]
      }
    }
  ];

  const togglePathway = (index) => {
    setExpandedPathway(expandedPathway === index ? null : index);
  };

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            Routes Into Canadian PR
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Work out which route fits your background, your working history and what you want to do next.
          </p>
        </motion.div>

        {/* Pathways */}
        <div className="space-y-6">
          {pathways.map((pathway, index) => {
            const Icon = pathway.icon;
            const isExpanded = expandedPathway === index;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                {/* Pathway Header */}
                <button
                  onClick={() => togglePathway(index)}
                  className="w-full p-6 flex items-start gap-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={24} className="text-white" />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-primary mb-2">
                      {pathway.title}
                    </h3>
                    <p className="text-sm text-gray-500 font-light mb-2">
                      {pathway.subtitle}
                    </p>
                    <p className="text-gray-600 text-sm font-light">
                      {pathway.description}
                    </p>
                  </div>

                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronUp size={24} className="text-secondary" />
                    ) : (
                      <ChevronDown size={24} className="text-secondary" />
                    )}
                  </div>
                </button>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 border-t border-gray-200">
                        <div className="pt-6 space-y-6">
                          {/* Overview */}
                          <div>
                            <h4 className="text-base font-semibold text-primary mb-2">
                              Overview
                            </h4>
                            <p className="text-gray-600 text-sm font-light leading-relaxed">
                              {pathway.details.overview}
                            </p>
                          </div>

                          {/* Programs/Categories */}
                          <div>
                            <h4 className="text-base font-semibold text-primary mb-3">
                              Streams & Requirements
                            </h4>
                            <div className="space-y-4">
                              {pathway.details.programs.map((program, pIndex) => (
                                <div key={pIndex} className="bg-surface p-4 rounded-lg">
                                  <h5 className="text-sm font-semibold text-primary mb-2">
                                    {program.name}
                                  </h5>
                                  <p className="text-gray-600 text-sm font-light leading-relaxed">
                                    {program.requirements}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Key Points */}
                          <div>
                            <h4 className="text-base font-semibold text-primary mb-3">
                              At a Glance
                            </h4>
                            <ul className="space-y-2">
                              {pathway.details.keyPoints.map((point, kIndex) => (
                                <li key={kIndex} className="flex items-start gap-2">
                                  <span className="w-1.5 h-1.5 bg-secondary rounded-full mt-2 flex-shrink-0"></span>
                                  <span className="text-gray-600 text-sm font-light">
                                    {point}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PRPathways;
